import React from 'react';
import { FeedbackBoxContainer } from './FeedbackBoxContainer';
import { Text } from '@/hen-ui/atomics/Text';
import { Icon } from '@/hen-ui/atomics/Icon';
import { Button } from '@/hen-ui/atomics/Button';

/**
 * Props for the FeedbackBox composite component.
 * Aligns with EDU Design System and WCAG 2.1 AA requirements for feedback banners.
 */
export interface FeedbackBoxProps extends React.PropsWithChildren {
  /** Feedback message to display. Required. */
  children: React.ReactNode;
  /** Callback when the close button is activated. If provided, close button can be shown (see closeIcon). */
  onClose?: () => void;
  /**
   * Controls close button visibility.
   * Default: true when onClose is provided, false otherwise.
   * Close button renders only if onClose is provided or closeIcon is explicitly true.
   */
  closeIcon?: boolean;
}

/**
 * FeedbackBox — standardized feedback banner for HEN applications.
 *
 * Combines Text and Icon atomics inside a styled container (secondary-50 background,
 * EDU design tokens). Use for informational messages that can optionally be dismissed.
 *
 * - Built from Text (message) and Icon (X) atomics; close control is a Button for keyboard access.
 * - Styling uses EDU Design System tokens only (no hardcoded values).
 * - When dismissible: role="alert", aria-live="polite", and keyboard-accessible close (Tab, Enter/Space).
 *
 * @see Acceptance: Feedback Box composite, EDU Design System, WCAG 2.1 AA
 */
export const FeedbackBox: React.FC<FeedbackBoxProps> = ({
  children,
  onClose,
  closeIcon,
}) => {
  return (
    <FeedbackBoxContainer>
      <Text variant='l1' className='flex-1'>
        {children}
      </Text>
      {closeIcon && (
        <Button
          type='button'
          variant='textOnly'
          onClick={() => onClose?.()}
          aria-label='Close feedback'
        >
          <Icon size='sm' icon='x' color='black' aria-hidden />
        </Button>
      )}
    </FeedbackBoxContainer>
  );
};
